import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetVendor } from 'src/services/vendor.service';
import { Vendor } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import VendorActionMenuButton from 'src/components/ActionButtons/VendorActionMenu';
import { loadData } from 'src/utils/grid.utils';

const VendorManagementGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const { loading, vendors, fetchVendors, deleteVendor, totalItems } =
    useGetVendor();
  const [sortBy, setSortBy] = useState<string>('organizationName');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'asc' },
  ]);

  useEffect(() => {
    fetchVendors(page, pageSize, searchTerm, sortBy, order);
  }, [state, pageSize, searchTerm, page, sortBy, order]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    let sortBy = newSortModel[0]?.field || 'organizationName';
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    if (sortBy === 'status') {
      sortBy = 'isActive';
    }
    setPage(1);
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'organizationName', headerName: 'Organization', flex: 1 },
    {
      field: 'contactPersonName',
      headerName: 'Point of Contact',
      flex: 1,
      sortable: false,
    },
    {
      field: 'contactNo',
      headerName: 'Contact Number',
      flex: 1,
      sortable: false,
    },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const vendor = vendors.find((v) => v.id === params.id);
        return vendor ? (
          <VendorActionMenuButton
            vendor={vendor}
            fetchVendors={fetchVendors}
            deleteVendor={deleteVendor}
          />
        ) : null;
      },
    },
  ];

  const transformData = (vendors: Vendor[]) => {
    try {
      return vendors.map((vendor, index) => ({
        id: vendor.id,
        number: (page - 1) * pageSize + index + 1,
        organizationName: vendor.organizationName,
        contactPersonName: vendor.contactPersonName,
        contactNo: vendor.contactNo,
        email: vendor.email,
        status: vendor.isActive ? 'Active' : 'Inactive',
      }));
    } catch (error) {
      showSnackbar('Error transforming vendor data', 'error');
      return [];
    }
  };

  const rows = transformData(vendors);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        sortModel={sortModel}
        disableColumnMenu
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default VendorManagementGrid;
